import { useState, useEffect, useCallback } from 'react';
import { menuSchedule } from 'data/data';
import { calculateRestaurantStatus } from './useRestaurantStatus';

interface UseDeliveryAvailabilityProps {
  menuScheduleData: menuSchedule[] | null;
  updateInterval?: number; // in milliseconds, default 60000 (1 minute)
}

export const useDeliveryAvailability = ({ 
  menuScheduleData, 
  updateInterval = 60000 
}: UseDeliveryAvailabilityProps) => {
  const [isDeliveryAvailable, setIsDeliveryAvailable] = useState<boolean>(false);
  const [isRestaurantOpen, setIsRestaurantOpen] = useState<boolean>(false);

  const calculateDeliveryAvailability = useCallback((): boolean => {
    if (!menuScheduleData) return false;

    // Calculate restaurant status
    const restaurantStatus = calculateRestaurantStatus(menuScheduleData);
    const isOpen = restaurantStatus.isOpen;
    
    // Update restaurant open status
    setIsRestaurantOpen(isOpen);
    
    // Delivery is available when restaurant is open
    return isOpen;
  }, [menuScheduleData]);

  // Update delivery availability immediately when component mounts or menuScheduleData changes
  useEffect(() => {
    const availability = calculateDeliveryAvailability();
    setIsDeliveryAvailable(availability);
  }, [calculateDeliveryAvailability]);

  // Set up interval to check delivery availability periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const availability = calculateDeliveryAvailability();
      setIsDeliveryAvailable(availability);
    }, updateInterval);

    return () => clearInterval(interval);
  }, [calculateDeliveryAvailability, updateInterval]);

  return {
    isDeliveryAvailable,
    isRestaurantOpen,
    // Helper function to check if delivery should be disabled
    isDeliveryDisabled: !isDeliveryAvailable
  };
};